import { authService } from "services";
import { isAdmin } from "utils/utils";
import {
	getOnboardingSteps,
	ONBOARDING_ROUTE,
	ONBOARDING_ENABLED,
	extractList,
	mergeStepsWithCompanyFlags,
	countCompletedSteps,
	didJustCompleteAll,
} from "../config/onboardingSteps";
import { onboardingStorage } from "./onboardingStorage";

/**
 * Store singleton (pub/sub) del onboarding.
 *
 * Vive a nivel de módulo para que el estado sobreviva a los remontajes de
 * Layout (cada página monta su propio Layout) y para que la card del sidebar y
 * el checklist del Dashboard compartan exactamente el mismo estado sin duplicar
 * peticiones. No usamos Context porque no hay un ancestro común estable.
 *
 * `dismissed` refleja `companies.onboarding_complete` (servidor).
 * El completado de cada paso refleja `companies.onboarding_*_complete`.
 */

const CACHE_TTL_MS = 30 * 1000; // recalcula la preview si los datos son viejos

const buildInitialSteps = () =>
	getOnboardingSteps().map((s) => ({
		key: s.key,
		done: false,
		items: [],
	}));

let state = {
	companyId: null,
	loading: false,
	loaded: false,
	lastFetch: 0,
	steps: buildInitialSteps(),
	completedCount: 0,
	total: getOnboardingSteps().length,
	dismissed: false,
	/** true solo tras leer `onboarding_complete` del company (evita flash). */
	flagLoaded: false,
	showChecklist: false,
};

const listeners = new Set();
let inFlight = null;

/**
 * Id numérico de la empresa activa.
 * Ojo: `authService.currentCompanyId` guarda el company_name (a menudo cifrado)
 * para el switch multi-empresa, no sirve para GET/PUT `/companies/{id}`.
 */
const getCompanyId = () =>
	(authService.currentUser &&
		authService.currentUser.company &&
		authService.currentUser.company.id) ||
	(authService.currentUser && authService.currentUser.company_id) ||
	null;

const emit = () => {
	listeners.forEach((l) => l(state));
};

const setState = (patch) => {
	state = { ...state, ...patch };
	emit();
};

// Si cambia la empresa activa, reseteamos el estado; el flag se lee del company.
const syncCompany = () => {
	const companyId = getCompanyId();
	if (companyId !== state.companyId) {
		onboardingStorage.clearCache();
		state = {
			...state,
			companyId,
			loaded: false,
			lastFetch: 0,
			steps: buildInitialSteps(),
			completedCount: 0,
			total: getOnboardingSteps().length,
			showChecklist: false,
			dismissed: false,
			flagLoaded: false,
		};
		emit();
	}
};

const loadStepPreviews = async () => {
	const onboardingSteps = getOnboardingSteps();
	const previews = await Promise.all(
		onboardingSteps.map((step) =>
			Promise.resolve()
				.then(() => step.fetchItems())
				.then((res) => ({
					key: step.key,
					items: extractList(res),
				}))
				.catch(() => ({ key: step.key, items: [] }))
		)
	);

	return { previews, onboardingSteps };
};

const runDetection = async () => {
	const [{ previews, onboardingSteps }, company] = await Promise.all([
		loadStepPreviews(),
		onboardingStorage.fetchCompany(getCompanyId()),
	]);

	const steps = mergeStepsWithCompanyFlags(
		onboardingSteps,
		company,
		previews
	);
	const completedCount = countCompletedSteps(steps);

	setState({
		steps,
		completedCount,
		total: onboardingSteps.length,
		dismissed: company?.onboarding_complete === true,
		flagLoaded: true,
		loading: false,
		loaded: true,
		lastFetch: Date.now(),
	});
	inFlight = null;
	return state;
};

export const onboardingStore = {
	getState() {
		return state;
	},

	subscribe(listener) {
		listeners.add(listener);
		return () => listeners.delete(listener);
	},

	/**
	 * La feature está activa sólo si el flag global está encendido y el usuario
	 * es administrador. Es el único punto por el que pasan todas las puertas del
	 * onboarding (sidebar, página, redirección de login, detección).
	 */
	isEnabled() {
		return ONBOARDING_ENABLED && isAdmin();
	},

	/**
	 * Carga la detección si hace falta. Dedupe por petición en vuelo y respeta
	 * el TTL salvo que se fuerce.
	 */
	ensureLoaded(force = false) {
		if (!this.isEnabled()) return Promise.resolve(state);

		syncCompany();

		const isStale = Date.now() - state.lastFetch > CACHE_TTL_MS;
		if (!force && state.loaded && !isStale) {
			return Promise.resolve(state);
		}
		if (inFlight) return inFlight;

		setState({ loading: true });
		inFlight = runDetection();
		return inFlight;
	},

	refresh() {
		return this.ensureLoaded(true);
	},

	async setStepComplete(stepKey, complete) {
		const wasComplete = selectIsComplete(state);
		const steps = state.steps.map((step) =>
			step.key === stepKey ? { ...step, done: !!complete } : step
		);
		const completedCount = countCompletedSteps(steps);
		setState({ steps, completedCount });

		const ok = await onboardingStorage.persistStepComplete(
			getCompanyId(),
			stepKey,
			complete
		);

		if (!ok) {
			await this.refresh();
			return { ok: false, justCompletedAll: false };
		}

		return {
			ok: true,
			justCompletedAll: didJustCompleteAll(
				wasComplete,
				selectIsComplete(state)
			),
		};
	},

	async dismiss() {
		setState({ dismissed: true, flagLoaded: true, showChecklist: false });
		const ok = await onboardingStorage.persistComplete(getCompanyId(), true);
		if (!ok) {
			setState({ dismissed: false, flagLoaded: true });
		}
		return ok;
	},

	async restore() {
		setState({ dismissed: false, flagLoaded: true });
		const ok = await onboardingStorage.persistComplete(getCompanyId(), false);
		if (!ok) {
			setState({ dismissed: true, flagLoaded: true });
		}
		return ok;
	},

	/**
	 * Vuelve a mostrar el onboarding con el progreso a cero.
	 * Persiste `onboarding_complete=false` y desmarca cada paso.
	 */
	async restart() {
		const ok = await onboardingStorage.persistRestart(getCompanyId());
		if (!ok) return false;

		if (inFlight) {
			try {
				await inFlight;
			} catch (e) {
				// la detección en curso no debe bloquear el reinicio
			}
		}

		const baseSteps = state.steps.length ? state.steps : buildInitialSteps();
		setState({
			steps: baseSteps.map((step) => ({ ...step, done: false })),
			completedCount: 0,
			total: getOnboardingSteps().length,
			dismissed: false,
			flagLoaded: true,
			loaded: true,
			loading: false,
			lastFetch: Date.now(),
			showChecklist: false,
		});
		return true;
	},

	setShowChecklist(show) {
		setState({ showChecklist: !!show });
	},
};

/**
 * Decide a dónde llevar al usuario justo después del login o del alta de cuenta.
 *
 * Si es admin y el onboarding no está cerrado (`onboarding_complete`), se le
 * envía a la página de onboarding aunque tenga los 6 pasos marcados. Cerrar
 * el onboarding es una acción explícita del usuario.
 */
export async function resolvePostLoginRedirect(defaultPath = "/") {
	if (!onboardingStore.isEnabled()) return defaultPath;
	try {
		const s = await onboardingStore.refresh();
		if (s.dismissed) return defaultPath;
		return ONBOARDING_ROUTE;
	} catch (e) {
		return defaultPath;
	}
}

/** Helpers derivados (puros) para no repetir lógica en los componentes. */
export const selectIsComplete = (s) =>
	s.total > 0 && s.completedCount >= s.total;

export const selectSidebarVisible = (s) =>
	onboardingStore.isEnabled() &&
	s.flagLoaded &&
	!s.dismissed &&
	s.total > 0;

export const selectChecklistVisible = (s) =>
	onboardingStore.isEnabled() && s.flagLoaded && !s.dismissed;
